/* eslint-disable no-unused-expressions */
/* eslint-disable jsx-a11y/label-has-associated-control */
import { memo, useEffect, useReducer, useState } from 'react';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { Popover, OverlayTrigger } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { add, isAfter } from 'date-fns';

import ActionButtonApproval from '../../../ApprovalPending/ViewApprovalPendingContainer/TableApproval/ActionButtonApproval';
import baseStyle from '../../styles/base.module.scss';
import { optimisticInitialState, optimisticUIReducer } from '../../reducer/optimisticUIReducer';
import { getWeeklyReportActions } from '../../../../services/workReport';
import useToast from '../../../../hooks/useToast';
import { workReportLineStatus } from '../../../../utils/dataStatus';

import { isVisibleEdit, isVisibleReview } from './helpers/actions-visibility ';
import styles from './DayCell.module.scss';

/**
 * @typedef Props
 * @property {number} columnNumber
 * @property {import('../../../../adapters/weekly-report/get-weekly-report-adapter').DailyReport | string} dailyReport
 * @property {import('../../hooks/useFilters').Filters} filters
 * @property {(id: string, idParteDiario: number) => void} handleDay
 * @property {(id: string) => void} handleDisableDay
 * @property {boolean} isChecked
 * @property {boolean} isInspector
 * @property {string} line
 * @property {string} lineID
 * @property {string} locationName
 * @property {string} measureUnit
 * @property {string} position
 */

/** @param {Props} */
const DayCell = ({
  columnNumber,
  dailyReport,
  filters,
  handleDay,
  handleDisableDay,
  isChecked,
  isInspector,
  line,
  lineID,
  locationName,
  measureUnit,
  position
}) => {
  const [showActionsModal, toggleActionsModal] = useReducer((state) => !state, false);
  const [enabledActions, setEnabledActions] = useState(null);
  const [isLoading, setIsLoading] = useState(null);
  const { setNewToast } = useToast();

  const [changedActions, dispatch] = useReducer(optimisticUIReducer, optimisticInitialState);

  const fetchActions = async () => {
    try {
      setIsLoading(true);
      const data = await getWeeklyReportActions(dailyReport.idParteDiario, isInspector);

      // When the query ends and the Imputation change flag is true, the imputation object icon is painted
      if (data.cambioImputacion) {
        dispatch({ type: 'imputation.daily' });
      }
      setEnabledActions(data);
    } catch (error) {
      setNewToast({
        type: 'ERROR',
        message: error.message
      });
      toggleActionsModal();
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (showActionsModal && typeof dailyReport !== 'string') {
      fetchActions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showActionsModal]);

  if (typeof dailyReport === 'string') {
    return (
      <td aria-label="celda" data-row-col={columnNumber}>
        <div className={`${styles['day-cell-container']} ${styles['empty-cell']}`} />
      </td>
    );
  }

  const getRawQuantity = (cantidadParam) =>
    changedActions.quantity !== undefined && changedActions.quantity !== null
      ? changedActions.quantity
      : cantidadParam ?? null;

  const {
    cantidad,
    colorInspeccion,
    coloresEstado,
    estadoEnum,
    fecha,
    habilitaCheck,
    idParteDiario,
    rechazaCantidad
  } = dailyReport;

  const { fechaDesde, fechaHasta } = filters;

  const startDay = add(new Date(fechaDesde), { days: -1 });
  const endDay = add(new Date(fechaHasta), { days: 1 });

  const isInDateRange = isAfter(new Date(fecha), startDay) && isAfter(endDay, new Date(fecha));

  const [textColor, bgCell] = coloresEstado.split(',');
  const [statusTextColor, statusBgColor] = changedActions.statusColors.split(',');

  const color = statusTextColor || textColor;
  const rawQuantity = getRawQuantity(cantidad);

  // show '--' only if the zeroAsDash flag is active and the value is exactly 0
  const quantity =
    (rawQuantity === 0 && changedActions.zeroAsDash) ||
    (Number(cantidad) === 0 && estadoEnum === workReportLineStatus.pendiente)
      ? '--'
      : rawQuantity ?? '--';
  const background = quantity === '--' ? '#E8E8E8' : statusBgColor || bgCell;

  const inspectionColor =
    quantity === '--' ? '#E8E8E8' : changedActions.inspectionColor || colorInspeccion;

  const handleCheckBox = () => {
    handleDay(lineID, idParteDiario);
  };

  /** @param {import('../../reducer/optimisticUIReducer').OptimisticUIActions} action */
  const handleChangedActions = (action) => {
    dispatch(action);
    if (
      action.type === 'approver.fixed' ||
      action.type === 'approver.returned' ||
      action.type === 'inspector.rejected' ||
      action.type === 'inspector.inspected'
    ) {
      handleDisableDay(lineID);
      // Remove the check if present when
      // performing an action that blocks the cell
      isChecked && handleDay(lineID, idParteDiario);
    }
  };

  const popoverClass = `${styles.popover} ${isLoading ? styles.disabled : ''}`;

  return (
    <td data-row-col={columnNumber}>
      <div className={styles['day-cell-container']} data-day-cell-disabled={!isInDateRange}>
        <div className={styles['day-cell-upper-container']} style={{ background }}>
          <OverlayTrigger
            onToggle={toggleActionsModal}
            overlay={
              <Popover
                className={popoverClass}
                id="popover-positioned-left"
                onClick={toggleActionsModal}
              >
                <Popover.Body>
                  <ActionButtonApproval
                    changedActions={changedActions}
                    dailyWorkReport={{
                      ...dailyReport,
                      ...enabledActions,
                      cantidad: quantity,
                      lineaSap: line,
                      posicionSap: position,
                      unidadMedida: measureUnit,
                      ubicacion: locationName,
                      tienePuntosPrograma: enabledActions?.notificadaPorPuntoDePrograma
                    }}
                    handleChangedActions={handleChangedActions}
                    isExtentedView
                    selectedDate={fecha}
                    visibleAccept={false}
                    visibleComment={cantidad !== null}
                    visibleEdit={isVisibleEdit({
                      changedActions,
                      isInspector,
                      rectificaCantidad: enabledActions?.rectificaCantidad
                    })}
                    visibleExpertReview={isInspector && cantidad}
                    visibleFiles={cantidad !== null}
                    visibleLink
                    visibleModifyImputation={isInspector}
                    visibleReject={!isInspector && rechazaCantidad && !changedActions.statusColors}
                    visibleReview={isVisibleReview({
                      changedActions,
                      estadoEnum,
                      isInspector,
                      rectificaCantidad: enabledActions?.rectificaCantidad,
                      revisionExperta: enabledActions?.revisionExperta
                    })}
                  />
                </Popover.Body>
              </Popover>
            }
            placement="left"
            rootClose
            show={showActionsModal}
            trigger="click"
          >
            <button
              aria-label="Acciones"
              className={`${baseStyle['focus-ring']} ${styles.btn}`}
              style={{ color: background === '#FFFFFF' ? '#000' : color }}
              type="button"
            >
              <BsThreeDotsVertical />
            </button>
          </OverlayTrigger>
          <label className={styles['checkbox-container']} style={{ color }}>
            <input
              checked={isChecked && habilitaCheck}
              className={baseStyle['focus-ring']}
              disabled={
                !habilitaCheck || !cantidad || changedActions.statusColors || !isInDateRange
              }
              onChange={handleCheckBox}
              type="checkbox"
            />
            {quantity?.toLocaleString('es-AR')}
          </label>
        </div>
        <div className={styles['bottom-line']} style={{ background: inspectionColor }} />
      </div>
    </td>
  );
};

DayCell.propTypes = {
  columnNumber: PropTypes.number.isRequired,
  dailyReport: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]).isRequired,
  handleDay: PropTypes.func.isRequired,
  handleDisableDay: PropTypes.func.isRequired,
  isChecked: PropTypes.bool.isRequired,
  isInspector: PropTypes.bool.isRequired,
  filters: PropTypes.shape({
    fechaDesde: PropTypes.string,
    fechaHasta: PropTypes.string
  }).isRequired,
  line: PropTypes.string.isRequired,
  lineID: PropTypes.string.isRequired,
  locationName: PropTypes.string.isRequired,
  measureUnit: PropTypes.string.isRequired,
  position: PropTypes.string.isRequired
};

export default memo(
  DayCell,
  (prev, next) =>
    prev.isChecked === next.isChecked &&
    prev.dailyReport.habilitaCheck === next.dailyReport.habilitaCheck &&
    prev.handleDisableDay === next.handleDisableDay
);
